import { useState } from "react";
import { ExternalLink, Swords } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import dayjs from "dayjs";

import { Avatar, Badge, Button, Sheet, getInitials, tintForId } from "@/ui";
import { usePublicUserProfile, useCreateChallenge, useMyChallenges } from "@/features/queries";
import { useAuth } from "@/contexts/AuthContext";
import { useDailyChallengeLimit } from "@/features/challenges/useDailyChallengeLimit";

interface Props {
  userId: number | null;
  onClose: () => void;
}

/** Bottom sheet shown when any avatar/name is tapped around the app. */
export function UserProfileSheet({ userId, onClose }: Props) {
  return (
    <Sheet open={userId != null} onClose={onClose}>
      {userId != null && <UserProfileSheetBody userId={userId} onClose={onClose} />}
    </Sheet>
  );
}

function UserProfileSheetBody({ userId, onClose }: { userId: number; onClose: () => void }) {
  const { user: me } = useAuth();
  const { data, isLoading, error } = usePublicUserProfile(userId);
  const { data: myChallenges = [] } = useMyChallenges();
  const createChallenge = useCreateChallenge();
  const dailyLimit = useDailyChallengeLimit();
  const [challengeSent, setChallengeSent] = useState(false);

  if (isLoading) {
    return (
      <div style={{ padding: "24px 16px", textAlign: "center", color: "var(--fg2)" }}>
        Загружаем профиль…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ padding: "24px 16px", textAlign: "center", color: "var(--fg2)" }}>
        Не удалось загрузить профиль.
      </div>
    );
  }

  const name =
    [data.first_name, data.last_name].filter(Boolean).join(" ") ||
    data.username ||
    `Игрок #${data.id}`;

  const isSelf = me?.id === data.id;

  // Don't offer the challenge button if we already have a pending/accepted
  // challenge with this opponent — consistent with CreateChallengeDialog.
  const hasActiveChallenge = myChallenges.some(
    (c) =>
      (c.status === "pending" || c.status === "accepted") &&
      ((c.challenger.id === data.id && c.challenged.id === me?.id) ||
        (c.challenged.id === data.id && c.challenger.id === me?.id)),
  );

  const onChallenge = async () => {
    try {
      await createChallenge.mutateAsync({
        challenged_user_id: data.id,
        scheduled_at: null,
        spot_id: null,
      });
      setChallengeSent(true);
    } catch {
      /* error is shown inline below */
    }
  };

  return (
    <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Identity */}
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <Avatar
          initials={getInitials(data.first_name, data.last_name, data.username)}
          size={72}
          tint={tintForId(data.id)}
          src={data.photo_url}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 className="h2" style={{ margin: 0, fontSize: 22 }}>{name}</h2>
          {data.username ? (
            <a
              href={`https://t.me/${data.username}`}
              target="_blank"
              rel="noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                color: "var(--brand-orange)",
                textDecoration: "none",
                fontSize: 14,
                marginTop: 2,
              }}
            >
              @{data.username}
              <ExternalLink size={12} />
            </a>
          ) : (
            <div className="caption" style={{ color: "var(--fg3)" }}>@— (нет username)</div>
          )}
          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
            {data.is_admin && <Badge kind="brand">Админ</Badge>}
            {data.is_trainer && <Badge kind="success">Тренер</Badge>}
          </div>
        </div>
      </div>

      {/* Rating + W/L */}
      <div style={{ display: "flex", gap: 12 }}>
        <Stat label="Рейтинг" value={String(data.rating)} accent />
        <Stat label="Побед" value={String(data.wins)} />
        <Stat label="Поражений" value={String(data.losses)} />
      </div>

      {/* Mini rating chart (last 30 matches) */}
      {data.rating_history.length > 1 && (
        <div style={{ background: "var(--bg-section)", borderRadius: "var(--r-md)", padding: "12px 8px 8px" }}>
          <div className="caption" style={{ color: "var(--fg3)", padding: "0 8px 4px" }}>
            История рейтинга
          </div>
          <div style={{ height: 120, width: "100%" }}>
            <ResponsiveContainer>
              <LineChart data={data.rating_history}>
                <XAxis dataKey="date" hide />
                <YAxis hide domain={["dataMin - 20", "dataMax + 20"]} />
                <Tooltip
                  formatter={(v: number) => [v, "Рейтинг"]}
                  labelFormatter={(v: string) => dayjs(v).format("DD MMM")}
                  contentStyle={{
                    background: "var(--bg)",
                    border: "1px solid var(--separator)",
                    fontSize: 12,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="rating"
                  stroke="var(--brand-orange)"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Challenge CTA — only when looking at someone else */}
      {!isSelf && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {challengeSent ? (
            <div
              style={{
                background: "rgba(30,122,90,0.12)",
                color: "var(--fg-success, #1E7A5A)",
                padding: "10px 12px",
                borderRadius: "var(--r-md)",
                fontSize: 14,
                textAlign: "center",
              }}
            >
              Вызов отправлен.
            </div>
          ) : hasActiveChallenge ? (
            <div
              style={{
                background: "var(--bg-section)",
                color: "var(--fg2)",
                padding: "10px 12px",
                borderRadius: "var(--r-md)",
                fontSize: 13,
                textAlign: "center",
              }}
            >
              У вас уже есть активный вызов с этим игроком.
            </div>
          ) : dailyLimit.atLimit ? (
            <div
              style={{
                background: "var(--bg-section)",
                color: "var(--fg2)",
                border: "1px solid var(--separator)",
                padding: "10px 12px",
                borderRadius: "var(--r-md)",
                fontSize: 13,
                textAlign: "center",
              }}
            >
              На сегодня лимит вызовов исчерпан ({dailyLimit.used} из{" "}
              {dailyLimit.limit}). Попробуйте завтра.
            </div>
          ) : (
            <Button
              fullWidth
              onClick={onChallenge}
              disabled={createChallenge.isPending || dailyLimit.atLimit}
            >
              <Swords size={16} style={{ marginRight: 8 }} />
              {createChallenge.isPending ? "Отправляем…" : "Бросить вызов"}
            </Button>
          )}
          {createChallenge.error && (
            <div
              style={{
                background: "rgba(223,63,64,0.1)",
                color: "var(--fg-danger)",
                padding: "8px 12px",
                borderRadius: "var(--r-md)",
                fontSize: 13,
              }}
            >
              {(createChallenge.error as Error).message}
            </div>
          )}
        </div>
      )}

      <Button fullWidth variant="ghost" onClick={onClose}>
        Закрыть
      </Button>
    </div>
  );
}

function Stat({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
  return (
    <div
      style={{
        flex: 1,
        background: "var(--bg-section)",
        borderRadius: "var(--r-md)",
        padding: "10px 12px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 22,
          color: accent ? "var(--brand-orange)" : "var(--fg1)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
      <div className="caption" style={{ color: "var(--fg3)" }}>{label}</div>
    </div>
  );
}
